import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChartColumnIncreasing } from "lucide-react";
import { cn } from "@/lib/utils";
import { OperationsExecutionExportMenu } from "./OperationsExecutionExportMenu";
import {
  REVENUE_CHART_CARD_HEADER_GAP,
  REVENUE_CHART_CARD_HEADER_HEIGHT,
  REVENUE_CHART_CARD_ICON_STROKE_WIDTH,
} from "../revenue/constants";
import {
  SERVICES_CHART_CARD_ICON_COLOR,
  SERVICES_CONTENT_GAP,
  SERVICES_CONTENT_PADDING,
} from "../services/constants";
import { OPERATIONS_EXECUTION_SERIES } from "./constants";
import { OperationsKpiCards } from "./OperationsKpiCards";
import { OperationsExecutionChart } from "./OperationsExecutionChart";
import type { OperationsExecutionTrendPoint, OperationsSummary } from "./types";

interface OperationsExecutionWidgetProps {
  summary: OperationsSummary;
  executionTrend: OperationsExecutionTrendPoint[];
  onExportProcessingTime?: (format: "csv" | "xlsx") => void;
  onExportOnTimeRate?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
  className?: string;
}

export const OperationsExecutionWidget: React.FC<
  OperationsExecutionWidgetProps
> = ({
  summary,
  executionTrend,
  onExportProcessingTime,
  onExportOnTimeRate,
  exportDisabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const executionTitle = t(
    "analytics.operations.executionVsStandard",
    "Execution vs standard",
  );

  const executionSeries = useMemo(
    () =>
      OPERATIONS_EXECUTION_SERIES.map((item) => ({
        ...item,
        label: t(
          `analytics.operations.execution.series.${item.key}`,
          item.label,
        ),
      })),
    [t],
  );

  return (
    <div
      className={cn(
        "flex w-full max-w-full flex-col rounded-2xl border border-[#E5E5E5] bg-white font-sans",
        className,
      )}
      style={{
        padding: SERVICES_CONTENT_PADDING,
        gap: SERVICES_CONTENT_GAP,
      }}
    >
      <div
        className="flex w-full items-center justify-between"
        style={{
          minHeight: REVENUE_CHART_CARD_HEADER_HEIGHT,
          gap: REVENUE_CHART_CARD_HEADER_GAP,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: REVENUE_CHART_CARD_HEADER_GAP }}
        >
          <span
            className="flex shrink-0 items-center justify-center"
            style={{
              color: SERVICES_CHART_CARD_ICON_COLOR,
              width: 16,
              height: 16,
            }}
          >
            <ChartColumnIncreasing
              className="size-4 shrink-0"
              strokeWidth={REVENUE_CHART_CARD_ICON_STROKE_WIDTH}
              aria-hidden
            />
          </span>
          <h3 className="shrink-0 text-sm leading-normal font-semibold text-[#171717]">
            {executionTitle}
          </h3>
        </div>
        {onExportProcessingTime && onExportOnTimeRate ? (
          <OperationsExecutionExportMenu
            onExportProcessingTime={onExportProcessingTime}
            onExportOnTimeRate={onExportOnTimeRate}
            disabled={exportDisabled}
          />
        ) : null}
      </div>

      <OperationsKpiCards summary={summary} />

      <OperationsExecutionChart
        data={executionTrend}
        series={executionSeries}
        showLegend
      />
    </div>
  );
};
